import { Combobox as BaseCombobox } from '@base-ui/react/combobox';
import { ChevronDown } from 'lucide-react';
import type { ComponentProps } from 'react';
import { Input } from '@/components/ui/Input';
import { cx } from '@/cva.config';

export interface ComboboxItem<T extends string = string> {
  label: string;
  value: T;
}

export interface ComboboxProps<T extends string = string>
  extends Omit<
    ComponentProps<typeof BaseCombobox.Root>,
    'items' | 'onValueChange' | 'value'
  > {
  className?: string;
  items: ComboboxItem<T>[];
  onValueChange: (value: T | null) => void;
  placeholder?: string;
  size?: 'sm' | 'md';
  value: T | null;
}

export const Combobox = <T extends string = string>({
  className,
  items,
  onValueChange,
  placeholder,
  required = false,
  size = 'sm',
  value,
}: ComboboxProps<T>) => {
  const selectedItem = items.find((item) => item.value === value) ?? null;

  return (
    <BaseCombobox.Root
      items={items}
      itemToStringLabel={(item: ComboboxItem<T>) => item.label}
      onValueChange={(next: ComboboxItem<T> | null) =>
        onValueChange(next?.value ?? null)
      }
      required={required}
      value={selectedItem}
    >
      <div className="relative">
        <BaseCombobox.Input
          placeholder={placeholder}
          render={<Input className={cx('pr-8', className)} size={size} />}
        />
        <BaseCombobox.Trigger
          aria-label="Toggle options"
          className="absolute top-1/2 right-1.5 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded-sm text-foreground-muted transition-colors hover:text-foreground"
        >
          <ChevronDown className="size-3" />
        </BaseCombobox.Trigger>
      </div>
      <BaseCombobox.Portal>
        <BaseCombobox.Positioner sideOffset={4}>
          <BaseCombobox.Popup className="z-40 w-(--anchor-width) max-w-(--available-width) rounded-md border border-border bg-surface p-1 shadow-md">
            <BaseCombobox.Empty className="px-2 py-1 text-xs lg:text-sm text-foreground-muted">
              No matches
            </BaseCombobox.Empty>
            <BaseCombobox.List className="max-h-[min(15rem,var(--available-height))] overflow-y-auto overscroll-contain">
              {(item: ComboboxItem<T>) => (
                <BaseCombobox.Item
                  className="flex cursor-default items-center rounded-sm px-2 py-1 text-sm lg:text-base data-highlighted:bg-accent-soft data-highlighted:text-accent"
                  key={item.value}
                  value={item}
                >
                  {item.label}
                </BaseCombobox.Item>
              )}
            </BaseCombobox.List>
          </BaseCombobox.Popup>
        </BaseCombobox.Positioner>
      </BaseCombobox.Portal>
    </BaseCombobox.Root>
  );
};
